import React, { useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import ReactHtmlParser from "react-html-parser";
import {
  EventContext,
  AnalyticsContext,
  UserContext,
  GeoContext,
} from "context";
import { PrimaryTemplate } from "Layout/Templates";
import { MaxWidthContainer } from "Layout/MaxWidthContainer";
import { Modal, Media16By9, MediaVideo, ButtonPrimary } from "Components";
import { RingSpinner } from "Components/RingSpinner";
import { RegistrationForm } from "Components/Form";
import { PoweredBy } from "Components/PoweredBy";
import { ButtonOutlined } from "Components/Buttons/ButtonOutlined";
import { useMediaQuery } from "hooks/useMediaQuery";
import brandPatternImg from "assets/images/brand-halftone-pattern.png";
import brandLogoImgMidSouth from "assets/logos/logo-ford-mid_south-white.png";
import desktopArtistImg from "assets/images/desktop-main-artist-image.jpg";
import desktopBackgroundImage from "assets/images/desktop-background-image.jpg";
import desktopHeroImg from "assets/images/desktop-hero-image.jpg";
import desktopArtistCtaImage from "assets/images/desktop-single-image.jpg";
import desktopBrandCTAImg from "assets/images/desktop-build-image.jpg";
import mobileArtistImg from "assets/images/mobile-main-artist-image.jpg";
import mobileBackgroundImage from "assets/images/mobile-background-image.jpg";
import mobileHeroImg from "assets/images/mobile-hero-image.jpg";
import mobileArtistCtaImage from "assets/images/mobile-single-image.jpg";
import mobileBrandCTAImg from "assets/images/mobile-build-image.jpg";
import fordOval from "assets/logos/logo-ford-oval.png";
import { GlobalContent, PageContent } from "./staticContent";

type LayoutProps = GlobalContent &
  PageContent & {
    registered?: boolean;
    postCampaign?: boolean;
  };

export const Layout = React.memo(
  ({
    heading,
    subHeading,
    body,
    formHeading,
    formSubHeading,
    formLegal,
    submit,
    registered,
    postCampaign,
    quote,
    brandCta,
    artistCta,
    videoUrl,
  }: LayoutProps) => {
    const { event } = useContext(EventContext);
    const { logClick } = useContext(AnalyticsContext);
    const { userType, getUserRegistration } = useContext(UserContext);

    const [open, setOpen] = useState(false);
    const [video, setVideo] = useState<string | undefined>();
    const [loading, setLoading] = useState(false);

    const formRef = useRef<HTMLHeadingElement | null>(null);
    const isMobile = useMediaQuery();

    // logo
    const { brand: brandLogo } = event.logos;

    const linkClickHandler = (url: string, label: string, category: string) => {
      logClick({ label, url }, category);
    };

    const setModalVisible = (bool: boolean, link: string) => {
      setVideo(link);
      setOpen(bool);
    };

    const scrollToSection = (
      ref: React.MutableRefObject<HTMLHeadingElement | null>
    ) => {
      if (ref.current) {
        window.scrollTo({ top: ref.current.offsetTop });
        linkClickHandler("Register Below btn", "Register Below", "internal");
      }
    };

    const [fdaf, setFdaf] = useState<string | undefined>();
    const handleZipFdafChange = (zip: string) => {
      setFdaf(zip);
    };

    useEffect(() => {
      getUserRegistration().then((data) => {
        setZip(data?.ZIP);
      });
    }, [userType]);

    const [zip, setZip] = useState<string | undefined>();

    if (loading) {
      window.scrollTo(0, 0);
      return <RingSpinner />;
    }

    return (
      <PrimaryTemplate
        flexGrow
        showFooter
        registered={registered}
        postCampaign={postCampaign}
        zip={zip}
      >
        <Container>
          <HeroSection>
            <HeroContainer>
              <HeroImage
                src={isMobile ? mobileBackgroundImage : desktopBackgroundImage}
                alt="Artist image with dark blue overlay"
              />
              <FloatContent>
                {
                  <LogoFloat>
                    <Link
                      href={brandLogo.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        linkClickHandler(
                          brandLogo.link,
                          "Ford Homepage",
                          "header"
                        )
                      }
                    >
                      <Logo src={brandLogoImgMidSouth} />
                    </Link>
                  </LogoFloat>
                }
                <HeadingContainer>
                  {subHeading && <Body>{ReactHtmlParser(subHeading)}</Body>}
                  {heading && (
                    <MainHeading>{ReactHtmlParser(heading)}</MainHeading>
                  )}
                  {body && <Body>{ReactHtmlParser(body)}</Body>}
                </HeadingContainer>
                {!registered && !postCampaign && (
                  <ButtonOutlined onClick={() => scrollToSection(formRef)}>
                    Register Below
                  </ButtonOutlined>
                )}
                {(registered || postCampaign) && (
                  <PoweredBy zipCode={zip ? zip : fdaf} />
                )}
              </FloatContent>
            </HeroContainer>

            <HeroContent>
              <PatternImg src={brandPatternImg} />
              <ArtistImgReg
                src={isMobile ? mobileArtistImg : desktopArtistImg}
              />
              <BrandSignature>
                <FordSign src={fordOval} />
              </BrandSignature>
            </HeroContent>
          </HeroSection>
          <VideoSection>
            <CallOutContainer>
              {quote && <MainHeading>{ReactHtmlParser(quote)}</MainHeading>}
              <ButtonPrimary
                onClick={() => {
                  setModalVisible(true, videoUrl);
                  linkClickHandler(videoUrl, "Hero Video", "internal");
                }}
              >
                Watch Video
              </ButtonPrimary>
            </CallOutContainer>
          </VideoSection>

          <ArtistBackground src={isMobile ? mobileHeroImg : desktopHeroImg} />

          {!registered && !postCampaign && (
            <Content>
              <>
                {formHeading && (
                  <Heading ref={formRef}>
                    {ReactHtmlParser(formHeading)}
                  </Heading>
                )}
                {formSubHeading && (
                  <SubHeading>{ReactHtmlParser(formSubHeading)}</SubHeading>
                )}
                <RegistrationForm
                  formPart={0}
                  formLegal={formLegal}
                  submitButton={submit}
                  setLoading={setLoading}
                  handleZipFdafChange={handleZipFdafChange}
                />
              </>
            </Content>
          )}

          <SecondaryCTA>
            <BrandCTA>
              <ImgCTA src={isMobile ? mobileBrandCTAImg : desktopBrandCTAImg} />
              <SubHeading>{ReactHtmlParser(brandCta.heading)}</SubHeading>
              <ButtonPrimary
                onClick={() => {
                  window.open(brandCta.link, "_blank");
                  linkClickHandler(brandCta.link, "Build & Price", "external");
                }}
              >
                {brandCta.linkText}
              </ButtonPrimary>
            </BrandCTA>
            <ArtistCTA>
              <ImgCTA
                src={isMobile ? mobileArtistCtaImage : desktopArtistCtaImage}
              />
              <SubHeading>{ReactHtmlParser(artistCta.heading)}</SubHeading>
              <ButtonPrimary
                onClick={() => {
                  window.open(artistCta.link, "_blank");
                  linkClickHandler(artistCta.link, "Artist song", "external");
                }}
              >
                {artistCta.linkText}
              </ButtonPrimary>
            </ArtistCTA>
          </SecondaryCTA>
        </Container>

        <Modal open={open} setOpen={setModalVisible}>
          <Media16By9>
            <MediaVideo
              src={video}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </Media16By9>
        </Modal>
      </PrimaryTemplate>
    );
  }
);

export const Heading = styled.h2`
  font-size: 40px;
  div {
    white-space: nowrap;
    display: inline;
  }
  span.brand-color {
    color: #066fef;
  }
  span#mark {
    font-size: 21px;
    position: relative;
    top: -15px;
  }
`;

export const SubHeading = styled.h3`
  font-size: 20px;
`;

export const Body = styled.p`
  font-size: 1rem;
  z-index: 5;
  span {
    font-size: 10px;
    position: relative;
    top: -4px;
  }
`;

export const MainHeading = styled.h1`
  font-size: 60px;
  position: relative;
  span.brand-color {
    color: #066fef;
  }
  span#mark {
    font-size: 25px;
    position: relative;
    top: -15px;
  }
  @media (max-width: 950px) {
    font-size: 55px;
  }
`;

const Container = styled(MaxWidthContainer)`
  display: flex;
  flex-direction: column;
  padding: 0;
  gap: 0px;
`;

const Content = styled.div`
  width: 80%;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  padding: 70px 0;
  h1,
  h2,
  h3 {
    text-align: center;
  }
  @media ${(props) => props.theme.maxMedia.medium} {
    width: 100%;
    padding: 80px 24px 50px;
    h2 {
      font-size: 35px;
    }
  }
`;

const Link = styled.a`
  width: 100%;
`;

const Logo = styled.img`
  width: 100%;
  display: flex;
  max-width: 238px;
  @media ${(props) => props.theme.maxMedia.medium} {
    max-width: 151px;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  border-bottom-right-radius: 8px;

  @media ${(props) => props.theme.maxMedia.medium} {
    object-position: 50% 20%;
  }

  @media ${({ theme }) => theme.media.small} {
    object-fit: cover;
    object-position: 70% 50%;
  }
`;

const HeroSection = styled.div`
  width: 100%;
  display: flex;
  min-height: fit-content;
  align-items: flex-start;
  @media ${(props) => props.theme.maxMedia.medium} {
    flex-direction: column;
  }
`;

const HeroContainer = styled.div`
  width: 55%;
  display: flex;
  position: relative;
  @media ${(props) => props.theme.maxMedia.medium} {
    width: 100%;
  }
`;

const LogoFloat = styled.div`
  width: 100%;
  display: flex;
  z-index: 1;
  a {
    width: max-content;
  }

  @media ${({ theme }) => theme.maxMedia.medium} {
    justify-content: center;
  }
`;

const HeadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 120px;

  @media (max-width: 950px) {
    margin-top: 80px;
    max-width: 440px;
  }
  @media ${({ theme }) => theme.media.small} {
    ${MainHeading} {
      max-width: 310px;
    }
  }
`;

const FloatContent = styled.div`
  width: 70%;
  display: flex;
  flex-direction: column;
  padding: 120px 0;
  margin: auto;
  color: #fff;
  ${MainHeading} {
    margin-top: 0;
    margin-bottom: 0;
  }
  button {
    margin-top: 44px;
    width: 220px;
    z-index: 5;
  }

  ${LogoFloat} + ${Body} {
    margin-bottom: 20px;
  }

  @media (max-width: 950px) {
    width: 80%;
    padding: 52px 0 67px;
    button {
      margin-top: 30px;
    }
  }
  @media ${({ theme }) => theme.media.small} {
  }
`;

const HeroContent = styled.div`
  width: 45%;
  display: flex;
  flex-direction: column;
  position: relative;
  @media ${(props) => props.theme.maxMedia.medium} {
    width: 100%;
  }
`;

const PatternImg = styled.img`
  width: 65%;
  object-fit: cover;
  z-index: 1;
  position: absolute;
  top: 0;
  left: 0;
`;

const ArtistImgReg = styled.img`
  width: 75%;
  object-fit: cover;
  z-index: 1;
  margin: 11% auto 0;
  border-radius: 8px;
  @media ${(props) => props.theme.maxMedia.medium} {
    width: 80%;
    margin-bottom: 10px;
  }
`;

const VideoSection = styled.div`
  width: 100%;
  display: flex;
  @media ${(props) => props.theme.maxMedia.medium} {
    flex-direction: column-reverse;
  }
`;

const CallOutContainer = styled.div`
  width: 80%;
  display: flex;
  flex-direction: column;
  padding: 6% 0 7% 9%;
  button {
    width: 180px;
    font-size: 16px;
  }
  @media ${(props) => props.theme.maxMedia.medium} {
    width: 100%;
    align-items: flex-start;
    padding: 5% 40px 15%;
    img {
      max-width: 400px;
    }
    ${MainHeading} {
      max-width: 300px;
    }
  }
`;

const BrandSignature = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin: 52px auto -20px;

  @media ${(props) => props.theme.maxMedia.medium} {
    justify-content: flex-start;
    margin: 30px auto;
    padding: 0 24px;
  }
`;

const FordSign = styled.img`
  width: 60%;
  object-fit: contain;
  margin: 0 auto;
`;

const ArtistBackground = styled.img`
  width: 100%;
  object-fit: contain;
`;

const ImgCTA = styled.img`
  width: 100%;
  object-fit: cover;
  border-radius: 8px;
`;

const SecondaryCTA = styled.div`
  width: 100%;
  margin: 0 auto;
  max-width: 1200px;
  display: flex;
  gap: 20px;
  padding: 100px 70px 0;
  @media ${(props) => props.theme.maxMedia.medium} {
    flex-direction: column;
    gap: 40px;
    padding: 35px 24px;
  }
`;

const BrandCTA = styled.div`
  width: 58.5%;
  display: flex;
  flex-direction: column;
  gap: 25px;
  img {
    aspect-ratio: 1.5;
  }
  button {
    width: 220px;
  }
  @media ${(props) => props.theme.maxMedia.medium} {
    width: 100%;
    button {
      margin-top: -25px;
    }
  }
`;

const ArtistCTA = styled.div`
  gap: 25px;
  width: 41.5%;
  flex-direction: column;
  display: flex;
  img {
    aspect-ratio: 1.06;
  }
  button {
    width: 220px;
  }
  @media ${(props) => props.theme.maxMedia.medium} {
    width: 100%;
    button {
      margin-top: -25px;
    }
  }
`;
